import { useMemo, useState } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatCurrency, formatNumber } from '@halo/web-lib';
import type { HaloEffectDailyRow } from '@halo/web-types';

// ─── Series config ────────────────────────────────────────────────────────────

type DriverKey = 'gmvMaxSpend' | 'metaClicks' | 'tiktokShopVisits' | 'videoViews';
type RevenueKey = 'shopifyRevenue' | 'amazonRevenue' | 'tiktokShopRevenue';
type ScaleMode = 'index' | 'raw' | 'zscore';

interface DriverConfig {
  key: DriverKey;
  label: string;
  color: string;
  fmt: 'usd' | 'num';
}

interface RevenueConfig {
  key: RevenueKey;
  label: string;
  color: string;
}

const DRIVERS: readonly DriverConfig[] = [
  { key: 'gmvMaxSpend', label: 'GMV Max Spend', color: '#8b5cf6', fmt: 'usd' },
  { key: 'metaClicks', label: 'Meta Clicks', color: '#3b82f6', fmt: 'num' },
  { key: 'tiktokShopVisits', label: 'TikTok Shop Visits', color: '#ec4899', fmt: 'num' },
  { key: 'videoViews', label: 'TikTok Video Views', color: '#06b6d4', fmt: 'num' },
];

const REVENUES: readonly RevenueConfig[] = [
  { key: 'shopifyRevenue', label: 'Shopify Revenue', color: '#22c55e' },
  { key: 'amazonRevenue', label: 'Amazon Revenue', color: '#f59e0b' },
  { key: 'tiktokShopRevenue', label: 'TikTok Shop Revenue', color: '#ef4444' },
];

const SCALE_MODES: readonly { value: ScaleMode; label: string }[] = [
  { value: 'index', label: 'Index 0–100' },
  { value: 'raw', label: 'Raw' },
  { value: 'zscore', label: 'Z-score' },
];

const LAG_RANGE = 14;

const SERIES_BY_KEY = new Map<string, DriverConfig | RevenueConfig>([
  ...DRIVERS.map((d) => [d.key, d] as const),
  ...REVENUES.map((r) => [r.key, r] as const),
]);

// ─── Scaling ──────────────────────────────────────────────────────────────────

function buildScaler(values: readonly number[], mode: ScaleMode): (v: number) => number {
  if (mode === 'raw' || values.length === 0) return (v) => v;

  if (mode === 'index') {
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min;
    return (v) => (range === 0 ? 0 : ((v - min) / range) * 100);
  }

  // z-score
  const n = values.length;
  const mean = values.reduce((a, b) => a + b, 0) / n;
  const variance = values.reduce((a, b) => a + (b - mean) ** 2, 0) / n;
  const std = Math.sqrt(variance);
  return (v) => (std === 0 ? 0 : (v - mean) / std);
}

// ─── Component ──────────────────────────────────────────────────────────────────

interface DriversComparisonChartProps {
  daily: HaloEffectDailyRow[];
}

export function DriversComparisonChart({ daily }: DriversComparisonChartProps) {
  const [scaleMode, setScaleMode] = useState<ScaleMode>('index');
  const [lagDriver, setLagDriver] = useState<DriverKey>('gmvMaxSpend');
  const [lag, setLag] = useState(0);
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set());

  const toggle = (key: string) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const scalers = useMemo(() => {
    const map = new Map<DriverKey, (v: number) => number>();
    for (const d of DRIVERS) {
      map.set(
        d.key,
        buildScaler(
          daily.map((pt) => pt[d.key]),
          scaleMode,
        ),
      );
    }
    return map;
  }, [daily, scaleMode]);

  const chartData = useMemo(() => {
    return daily.map((pt, i) => {
      const row: Record<string, string | number | null> = { date: pt.date };
      for (const d of DRIVERS) {
        const srcIdx = d.key === lagDriver ? i - lag : i;
        const src = daily[srcIdx];
        const raw = src ? src[d.key] : null;
        row[d.key] = raw === null ? null : scalers.get(d.key)!(raw);
        row[`${d.key}Raw`] = raw;
      }
      for (const r of REVENUES) {
        row[r.key] = pt[r.key];
      }
      return row;
    });
  }, [daily, lagDriver, lag, scalers]);

  if (daily.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 p-8 text-center text-sm text-gray-500 dark:text-gray-400">
        No halo-effect daily data for this store/range.
      </div>
    );
  }

  const lagDriverLabel = DRIVERS.find((d) => d.key === lagDriver)?.label ?? '';
  const leftDomain: [number | string, number | string] =
    scaleMode === 'index' ? [0, 100] : ['auto', 'auto'];

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
      <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2">
        <h2 className="text-sm font-medium text-gray-700 dark:text-gray-200">Drivers vs revenue</h2>

        {/* Scaling toggle */}
        <div className="inline-flex overflow-hidden rounded-md border border-gray-300 dark:border-gray-700 text-xs">
          {SCALE_MODES.map((m) => (
            <button
              key={m.value}
              type="button"
              onClick={() => setScaleMode(m.value)}
              className={`px-2 py-1 transition-colors ${
                scaleMode === m.value
                  ? 'bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900'
                  : 'bg-white text-gray-600 hover:bg-gray-100 dark:bg-gray-900 dark:text-gray-300 dark:hover:bg-gray-800'
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>

        {/* Lag control */}
        <div className="ml-auto flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
          <span>Lag</span>
          <select
            className="rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-1.5 py-1"
            value={lagDriver}
            onChange={(e) => setLagDriver(e.target.value as DriverKey)}
          >
            {DRIVERS.map((d) => (
              <option key={d.key} value={d.key}>
                {d.label}
              </option>
            ))}
          </select>
          <input
            type="range"
            min={-LAG_RANGE}
            max={LAG_RANGE}
            step={1}
            value={lag}
            onChange={(e) => setLag(Number(e.target.value))}
            className="w-32 accent-gray-700 dark:accent-gray-300"
          />
          <span className="w-10 tabular-nums">{lag > 0 ? `+${lag}` : lag}d</span>
          <button
            type="button"
            onClick={() => setLag(0)}
            disabled={lag === 0}
            className="rounded-md border border-gray-300 dark:border-gray-700 px-2 py-1 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40"
          >
            Reset
          </button>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={340}>
        <ComposedChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 10, fill: '#9ca3af' }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            yAxisId="driver"
            domain={leftDomain}
            tickFormatter={(v: number) => (scaleMode === 'raw' ? formatNumber(v) : v.toFixed(0))}
            tick={{ fontSize: 10, fill: '#9ca3af' }}
            axisLine={false}
            tickLine={false}
            width={48}
          />
          <YAxis
            yAxisId="usd"
            orientation="right"
            tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`}
            tick={{ fontSize: 10, fill: '#9ca3af' }}
            axisLine={false}
            tickLine={false}
            width={48}
          />
          <RTooltip content={<DriversTooltip />} />

          {DRIVERS.filter((d) => !hidden.has(d.key)).map((d) =>
            d.key === lagDriver ? (
              <Area
                key={d.key}
                yAxisId="driver"
                type="monotone"
                dataKey={d.key}
                name={d.key}
                stroke={d.color}
                strokeWidth={2.5}
                fill={d.color}
                fillOpacity={0.12}
                connectNulls={false}
                dot={false}
                isAnimationActive={false}
              />
            ) : (
              <Line
                key={d.key}
                yAxisId="driver"
                type="monotone"
                dataKey={d.key}
                name={d.key}
                stroke={d.color}
                strokeWidth={1.75}
                dot={false}
                isAnimationActive={false}
              />
            ),
          )}

          {REVENUES.filter((r) => !hidden.has(r.key)).map((r) => (
            <Line
              key={r.key}
              yAxisId="usd"
              type="monotone"
              dataKey={r.key}
              name={r.key}
              stroke={r.color}
              strokeWidth={2.25}
              dot={false}
              isAnimationActive={false}
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>

      {/* Legend */}
      <div className="mt-2 flex flex-wrap gap-1.5">
        {[...DRIVERS, ...REVENUES].map((s) => {
          const active = !hidden.has(s.key);
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => toggle(s.key)}
              aria-pressed={active}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] transition-all ${
                active
                  ? 'border-gray-200 bg-white text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200'
                  : 'border-dashed border-gray-200 text-gray-400 line-through dark:border-gray-700'
              }`}
            >
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{
                  background: active ? s.color : 'transparent',
                  border: `1px solid ${s.color}`,
                }}
              />
              {s.label}
            </button>
          );
        })}
      </div>

      <p className="mt-2 text-[11px] text-gray-400">
        Drivers on the left axis (
        {scaleMode === 'index' ? 'indexed 0–100' : scaleMode === 'zscore' ? 'z-scored' : 'raw'}),
        revenue in $ on the right. {lagDriverLabel} shifted {lag > 0 ? `+${lag}` : lag} days
        {lag !== 0
          ? ` (${lag > 0 ? 'earlier activity aligned with later revenue' : 'later activity aligned with earlier revenue'})`
          : ''}
        . Tooltip shows raw values.
      </p>
    </div>
  );
}

// ─── Tooltip ──────────────────────────────────────────────────────────────────

interface TooltipEntry {
  dataKey?: string | number;
  color?: string;
  payload?: Record<string, string | number | null>;
}

function DriversTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: TooltipEntry[];
  label?: string | number;
}) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload ?? {};

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-xs shadow-lg">
      <p className="mb-1.5 font-medium text-gray-700 dark:text-gray-200">{String(label ?? '')}</p>
      {payload.map((entry, i) => {
        const key = String(entry.dataKey ?? '');
        const cfg = SERIES_BY_KEY.get(key);
        if (!cfg) return null;
        const isDriver = 'fmt' in cfg;
        const rawValue = isDriver ? row[`${key}Raw`] : row[key];
        const num = rawValue === null || rawValue === undefined ? null : Number(rawValue);
        const formatted =
          num === null
            ? '—'
            : isDriver && cfg.fmt === 'num'
              ? formatNumber(num)
              : formatCurrency(num);
        return (
          <div key={i} className="flex items-center justify-between gap-4">
            <span className="flex items-center gap-1.5 text-gray-500 dark:text-gray-300">
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ background: entry.color ?? '#888' }}
              />
              {cfg.label}
            </span>
            <span className="font-semibold tabular-nums text-gray-800 dark:text-white">
              {formatted}
            </span>
          </div>
        );
      })}
    </div>
  );
}
